import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { withStyles, withTheme } from '@mui/styles';
import styles from './ConceptTree.styles';
import services from 'services';
import Progress from 'studio/components/Progress';
import _ from 'lodash';
import { availableGroups } from 'store/utils/knowledgeGraphOntologyIcons';
import LinkIcon from '@mui/icons-material/LinkRounded';
import FunctionsIcon from '@mui/icons-material/FunctionsRounded';
import FunctionIcon from 'svg/knowledgeGraph/function.svg';
import DiscoveryIcon from 'svg/knowledgeGraph/discovery.svg';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadlineRounded';
import FolderIcon from '@mui/icons-material/FolderRounded';
import QuestionAnswer from '@mui/icons-material/QuestionAnswerRounded';
import Dashboard from '@mui/icons-material/Dashboard';
import ChatIcon from '@mui/icons-material/ChatRounded';
import Tree from 'components/Tree/Tree';
import { sortBy } from 'utils/arrayUtils';
import classNames from 'classnames';
import { usePrevious } from 'studio/helpers';
import { fileTypes } from 'studio/pages/Editor/FileTypeConstants';
import SyncIcon from '@mui/icons-material/SyncRounded';
import { KgPermissions } from 'studio/constants';
import { useDeleteAction } from './useDeleteAction';
import { action } from './actionUtil';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const ClassCategory = 'class';
const FunctionCategory = 'function';
const VariableCategory = 'variable';
const DiscoveryCategory = 'discovery';
const GroundTruthCategory = 'ground-truth';
const BoardCategory = 'board';

export const KgConceptTypes = {
  class: 'class',
  classAttribute: 'attribute',
  coOccurrence: 'co-occurrence',
  discoveryCategory: 'discovery-category',
  discoveryTopic: 'discovery-topic',
  discoveryQuestion: 'discovery-question',
  function: 'function',
  variable: 'variable',
  groundTruth: 'ground-truth',
  board: 'board'
};

const IconsForVklType = {
  class: availableGroups['meta:KB_Class'].componentIcon,
  subclass: availableGroups['meta:KB_Class'].componentIcon,
  number: availableGroups['xsd:integer'].componentIcon,
  integer: availableGroups['xsd:integer'].componentIcon,
  long: availableGroups['xsd:long'].componentIcon,
  string: availableGroups['xsd:string'].componentIcon,
  boolean: availableGroups['xsd:boolean'].componentIcon,
  date: availableGroups['meta:Date'].componentIcon,
  relationship: LinkIcon,
  measure: FunctionsIcon, // Sum icon (big sigma)
  function: FunctionIcon, // Function icon (f x)
  variable: AttachMoneyIcon, // $ icon
  discovery_category: FolderIcon,
  discovery_topic: FolderIcon,
  discovery_question: ChatIcon,
  ground_truth: QuestionAnswer,
  rephrase_ground_truth: QuestionAnswer,
  board: Dashboard
};

/**
 * The categories that are at the top level of the concept tree
 *
 * E.g. the discovery top-level category is called “Discovery” and contains “Discovery Categories”,
 *  which in turn contain topics and questions (different types of concepts present).
 * Each category has one type of concept associated with it.
 */
const TopLevelCategories = {
  [ClassCategory]: {
    type: KgConceptTypes.class,
    label: 'Classes',
    icon: IconsForVklType.class,
    associatedFileTypes: [fileTypes.class, fileTypes.entities]
  },
  [FunctionCategory]: {
    type: KgConceptTypes.function,
    label: 'Functions',
    icon: IconsForVklType.function,
    associatedFileTypes: [fileTypes.computation]
  },
  [VariableCategory]: {
    type: KgConceptTypes.variable,
    label: 'Variables',
    icon: IconsForVklType.variable,
    associatedFileTypes: [fileTypes.variables]
  },
  [DiscoveryCategory]: {
    type: KgConceptTypes.discoveryCategory,
    label: 'Discovery',
    icon: DiscoveryIcon,
    associatedFileTypes: [fileTypes.discovery]
  },
  [GroundTruthCategory]: {
    type: KgConceptTypes.groundTruth,
    label: 'Ground Truths',
    icon: IconsForVklType.ground_truth,
    associatedFileTypes: [fileTypes.groundTruth]
  },
  [BoardCategory]: {
    type: KgConceptTypes.board,
    label: 'Boards',
    icon: IconsForVklType.board,
    associatedFileTypes: [fileTypes.board]
  }
};

/**
 * Configurations for each type of concept
 *
 * E.g. they tell us which types of concepts can be inside an concept of each type, or what additional actions are available.
 */
const ConceptTypeConfigurations = {
  [KgConceptTypes.class]: {
    childTypes: [KgConceptTypes.classAttribute, KgConceptTypes.class],
    parentConceptProperty: 'parentClass',
    getAdditionalActions: (cls, kgPermissions, open, openSyncDialog) => {
      const openEntitiesAction =
        cls.entitiesFile &&
        action('open-entities', ViewHeadlineIcon, 'Open entities file', () => open(cls.entitiesFile.path), true);
      const syncEntitiesAction =
        kgPermissions.includes(KgPermissions.KgSync) &&
        action(
          'sync-entities',
          SyncIcon,
          'Synchronize entities',
          () => openSyncDialog(cls.file.id, [cls.vklQualifiedIdentifier]),
          false,
          !cls.isSynchronizable
        );

      return _.compact([openEntitiesAction, syncEntitiesAction]);
    },
    getChildrenCount: cls => cls.childClassCount + cls.attributeCount
  },
  [KgConceptTypes.classAttribute]: {
    childTypes: [],
    parentConceptProperty: 'parentClass'
  },
  [KgConceptTypes.function]: {
    childTypes: []
  },
  [KgConceptTypes.variable]: {
    childTypes: []
  },
  [KgConceptTypes.discoveryCategory]: {
    childTypes: [KgConceptTypes.discoveryTopic],
    getChildrenCount: category => category.topicCount
  },
  [KgConceptTypes.discoveryTopic]: {
    childTypes: [KgConceptTypes.discoveryQuestion],
    parentConceptProperty: 'parentCategory',
    getChildrenCount: topic => topic.questionCount
  },
  [KgConceptTypes.discoveryQuestion]: {
    childTypes: [],
    parentConceptProperty: 'parentTopic'
  },
  [KgConceptTypes.groundTruth]: {
    childTypes: []
  },
  [KgConceptTypes.board]: {
    childTypes: []
  }
};

const ConceptTree = forwardRef(
  ({ classes: cssClasses, className, knowledgeGraph, selectedDefinition, open, onDelete, openSyncDialog }, ref) => {
    // loading flags, indexed by tree item id, specifying if the children of a given tree item are being loaded
    // Also stores additional loading flags for loading children, indexed by the tree item ID and the child type
    const [loadingFlags, setLoadingFlags] = useState({});

    // the concepts to be displayed in the tree
    // They are structured as follows:
    // - The state contains a JS object with each top-level type as key
    // - The values are JS objects containing these concepts, with the key being the URI and the value the concept as returned by the API
    // - Any child concepts are nested inside the parent concept under the key 'treeChildren' (augmenting the original concept returned by the API)
    const [kgConcepts, setKgConcepts] = useState({});

    // the IDs of the class tree items to expand (only for classes atm!)
    const [expandedClassTreeItemIds, setExpandedClassTreeItemIds] = useState(null);

    // specifies whether the inital data is loading
    // this is used exclusively at the beginning to avoid rendering the tree before all data is loaded, making loading less busy
    const [isLoadingForFirstTime, setIsLoadingForFirstTime] = useState(true);

    // the ID of the knowledge graph
    const kgId = useMemo(() => knowledgeGraph?.data.id, [knowledgeGraph]);
    const kgPermissions = useMemo(() => knowledgeGraph?.data.kgPermissions || [], [knowledgeGraph]);

    const getDeleteAction = useDeleteAction(kgPermissions, onDelete);

    // reference storing the knowledge graph ID s.t. the up-to-date value can be checked in request callbacks
    // see https://stackoverflow.com/a/60643670
    const kgIdRef = useRef();
    kgIdRef.current = kgId;

    // sets the loading flag for the specified ID for the duration of the specified operation
    const withLoadingFlag = (id, op) => {
      // set to '1' or increment the counter: this allows nesting / interleaving calls
      setLoadingFlags(flags => ({ ...flags, [id]: flags[id] + 1 || 1 }));
      return op().then(result => {
        // decrement the counter or unset the flag (don't use '0', which is falsy but gets rendered)
        setLoadingFlags(flags => ({ ...flags, [id]: flags[id] - 1 || undefined }));
        return result;
      });
    };

    // gets the parent (short) URI of the specified concept
    const getParentUri = concept => {
      return concept[ConceptTypeConfigurations[concept.type].parentConceptProperty]?.shortUri || null;
    };

    // merges child concepts, prefering concepts in new children over concepts in old children
    // will keep additional children from old children if keepOld is true, in particular it will keep any more deeply nested children
    function mergeChildren(newChildren, oldChildren, keepOld) {
      if (!newChildren) {
        return oldChildren;
      }
      if (!oldChildren) {
        return newChildren;
      }
      const urisToKeep = keepOld
        ? _.uniq(Object.keys(newChildren).concat(Object.keys(oldChildren)))
        : Object.keys(newChildren);
      return Object.fromEntries(
        urisToKeep.map(uri => {
          return [
            uri,
            {
              ...(newChildren?.[uri] || oldChildren?.[uri]),
              treeChildren: mergeChildren(newChildren?.[uri]?.treeChildren, oldChildren?.[uri]?.treeChildren, true)
            }
          ];
        })
      );
    }

    // replaces the concept at the specified path with the new concept (does not work for top-level categories)
    // Important: the concept must already be present in the tree!
    function replaceConcept(concepts, path, newConcept, isCategory, replaceOnlyChildren) {
      if (!(path[0] in concepts)) {
        return concepts;
      }

      let updatedChild;
      const concept = concepts[path[0]];
      if (isCategory) {
        updatedChild = replaceConcept(concept, path.slice(1), newConcept, false, replaceOnlyChildren);
      } else if (path.length === 1) {
        if (replaceOnlyChildren) {
          updatedChild = {
            ...concept,
            treeChildren: mergeChildren(newConcept.treeChildren, concept.treeChildren, false)
          };
        } else {
          updatedChild = {
            ...newConcept,
            treeChildren: mergeChildren(newConcept.treeChildren, concept.treeChildren, false)
          };
        }
      } else {
        updatedChild = {
          ...concept,
          treeChildren: replaceConcept(concept.treeChildren, path.slice(1), newConcept, false, replaceOnlyChildren)
        };
      }
      return {
        ...concepts,
        [path[0]]: updatedChild
      };
    }

    /**
     * Loads / reloads concepts of a top-level category
     */
    const reloadCategory = category => {
      withLoadingFlag(category, async () => {
        // get the top-level concepts, i.e. which have no parent
        const response = await services.getKgConcepts(kgId, TopLevelCategories[category].type, null);
        // only update the state if the knowledge graph hasn't been changed during the request
        if (kgIdRef?.current === kgId) {
          setKgConcepts(concepts => {
            const newConcepts = response.data.data;
            const newWithChildren = Object.fromEntries(
              newConcepts.map(concept => [
                concept.data.uri,
                { ...concept.data, treeChildren: concepts[category]?.[concept.data.uri]?.treeChildren }
              ])
            );
            return { ...concepts, [category]: newWithChildren };
          });
        }
      });
    };

    /** Loads / reloads the given concept
     *
     * Keeps the previous nested concepts (i.e. children of children).
     * Set `fetchChildrenFirstTime` to true if you always want to fetch the children, possibly for the first time,
     *  and to false if it's just a reload, and you don't want to fetch children that haven't been fetched yet.
     * Set `shouldReloadConceptItself` to false if the concept is anyway reloaded because the parent is reloaded.
     */
    const reloadConcept = (category, parentUris, concept, fetchChildrenFirstTime, shouldReloadConceptItself = true) => {
      const shouldReloadChildren = fetchChildrenFirstTime || concept.treeChildren;

      // Skip if we actually don't need to reload anything, this is very important for performance
      if (shouldReloadConceptItself || shouldReloadChildren) {
        withLoadingFlag(concept.uri, async () => {
          // get the concept itself (if requested)
          const mainPromise =
            shouldReloadConceptItself &&
            services.getKgConcept(kgId, concept.type, concept.shortUri, getParentUri(concept));

          // Reload children (if the children have already been loaded, or we want to load them for the first time)
          let childrenPromises = [];
          if (shouldReloadChildren) {
            childrenPromises = ConceptTypeConfigurations[concept.type].childTypes.flatMap(childType =>
              services.getKgConcepts(kgId, childType, concept.shortUri)
            );
          }

          const mainResponse = shouldReloadConceptItself && (await mainPromise);
          const childrenResponses = await Promise.all(childrenPromises);

          // only update the state if the knowledge graph hasn't been changed during the request
          if ((!shouldReloadConceptItself || mainResponse.success) && kgIdRef?.current === kgId) {
            // the new concept
            const children = childrenResponses.flatMap(c => c.data.data);
            const newConcept = {
              ...(shouldReloadConceptItself ? mainResponse.data.data : {}),
              treeChildren: shouldReloadChildren
                ? Object.fromEntries(children.map(child => [child.data.uri, child.data]))
                : undefined
            };

            // update the concept (if requested) and its children
            setKgConcepts(concepts =>
              replaceConcept(
                concepts,
                [category, ...parentUris, concept.uri],
                newConcept,
                true,
                !shouldReloadConceptItself
              )
            );
          }
        });
      }
    };

    const previousKnowledgeGraph = usePrevious(knowledgeGraph);

    // initializes state and retrieves data on first load and whenever the knowledge graph changes
    useEffect(() => {
      (async () => {
        if (knowledgeGraph && knowledgeGraph.data.id !== previousKnowledgeGraph?.data.id) {
          setIsLoadingForFirstTime(true);
          setKgConcepts({});
          setLoadingFlags({});

          await Promise.all(Object.keys(TopLevelCategories).map(category => reloadCategory(category)));
          setIsLoadingForFirstTime(false);
        }
      })();
    }, [knowledgeGraph, previousKnowledgeGraph]);

    // sorts the specified tree items by their labels
    const sort = treeItems =>
      // sort the tree items
      (treeItems && sortBy(treeItems, treeItem => treeItem.label.toLowerCase())) // sort the tree item's children recursively
        ?.map(treeItem => ({ ...treeItem, children: treeItem.children && sort(treeItem.children) }));

    // reloads all concepts in the category that match the predicate
    const reloadCategoryByPredicate = (category, shouldReload, isCategoryReloaded = false) => {
      // recursively get all concepts that should be reloaded (will return duplicates)
      const getConceptsToReload = (conceptsWithKeys, parentUris) => {
        const concepts = Object.values(conceptsWithKeys);
        const fromThisLevel = concepts.filter(shouldReload).map(obj => [parentUris, obj]);
        const fromNested = concepts.flatMap(obj =>
          getConceptsToReload(obj?.treeChildren || {}, [...parentUris, obj.uri])
        );

        return fromThisLevel.concat(fromNested);
      };

      // Make unique, and sort alphabetically (same as sort for tree items), to reload from top to bottom
      const conceptsToReload = _.sortBy(
        _.uniq(getConceptsToReload(kgConcepts[category] || {}, [])),
        ([, { vklIdentifier }]) => vklIdentifier.toLowerCase()
      );
      const allUrisOfConceptsToReload = new Set(conceptsToReload.map(c => c[1].shortUri));
      conceptsToReload.forEach(([parentUris, concept]) => {
        // Only need to reload concept itself if we don't already reload its parent
        // Note that we reload top-level concepts in any case
        const parentUri = getParentUri(concept);
        const skipReloadingConceptItself = parentUri ? allUrisOfConceptsToReload.has(parentUri) : isCategoryReloaded;
        reloadConcept(category, parentUris, concept, false, !skipReloadingConceptItself);
      });
    };

    /**
     * Reloads tree items based on the specified affected files.
     *
     * If no affected files are specified, then all tree items are reloaded.
     */
    const reloadTreeItems = async affectedFiles => {
      // the types of the affected files
      const affectedFileTypes = affectedFiles?.map(file => file.data.type);
      // the IDs of the affected files
      const affectedFileIds = affectedFiles?.map(file => file.data.id);
      // specifies whether to reload all concepts
      const reloadAll = !affectedFiles;
      // returns true if...
      const shouldReload = concept =>
        // ...all concepts should be reloaded
        reloadAll ||
        // ... or if it is contained in / affected by one of the affected files
        affectedFileIds.includes(concept.file.id) ||
        affectedFileIds.includes(concept.entitiesFile?.id);

      Object.entries(TopLevelCategories).forEach(([category, { associatedFileTypes }]) => {
        // reload top-level concepts of the category if we reload everything or if one of the modified files is of the associated type
        const shouldReloadCategory = reloadAll || associatedFileTypes.some(ft => affectedFileTypes.includes(ft));
        if (shouldReloadCategory) {
          reloadCategory(category);
        }

        reloadCategoryByPredicate(category, shouldReload, shouldReloadCategory);
      });
    };

    /**
     * Reloads the classes and their immediate children with the specified URIs
     *
     * Note: won't load new top-level classes.
     */
    const reloadClassTreeItems = uris => {
      reloadCategoryByPredicate(ClassCategory, cls => uris.includes(cls.uri));
    };

    // exposes functions by making them available on the component's reference
    useImperativeHandle(ref, () => ({
      reloadTreeItems,
      reloadClassTreeItems
    }));

    // returns click handler for a tree item which loads its children
    const treeItemClickHandler =
      (concept, children, loadChildren) => (event, isExpanding, isExpandCollapseIconClick) => {
        // if the tree item's children haven't been loaded yet...
        if (!children) {
          // ...load them
          loadChildren();
        }
        // if the click was not on the expand / collapse icon but on the tree item itself...
        if (!isExpandCollapseIconClick) {
          // ...then open the concept in the editor
          open(concept.file.path, concept.vklQualifiedIdentifier);
          // if the tree item is being collapsed...
          if (!isExpanding) {
            // ...then we prevent the collapsing to keep it expanded as we are opening the concept in the editor
            event.preventDefault();
          }
        }
      };

    // expands the class tree once based on the selected definition, only works for classes (and their attributes) currently
    // Note: relies on selectedDefinition being null to expand the class tree (!)
    useEffect(() => {
      if (!isLoadingForFirstTime && selectedDefinition !== undefined && !expandedClassTreeItemIds) {
        (async () => {
          // the chain of parent classes of the class / attribute represented by the selected definition
          const parentClasses = [];
          // Warning: comes from the definitions in the file!
          // The parent here might not be the same as in the concept tree (the parent here is exclusively based on nesting in VKL!)
          let parentClass = selectedDefinition?.parent;
          while (parentClass) {
            // Query child or top-level classes
            const response = await services.getKgConcept(kgId, KgConceptTypes.class, parentClass.shortUri, undefined);
            const cls = response.data.data;
            cls && parentClasses.unshift(cls);
            parentClass = cls?.parentClass;
          }
          const parentClassUris = parentClasses.map(cls => cls.uri);
          // add the selected definition class / attribute itself
          //  (warning: we always pretend it's a class for simplicity, as we don't have the required information)
          selectedDefinition && parentClasses.push({ ...selectedDefinition, type: KgConceptTypes.class });
          // load the class children for all parent classes
          parentClasses.forEach((parentClass, idx) => {
            // Only load concepts that are actually classes, otherwise there will be an error
            if (parentClass.vklType === 'class') {
              reloadConcept(ClassCategory, parentClassUris.slice(0, idx), parentClass, true);
            }
          });

          // the IDs of the class tree items to expand (note: we always expand the class category itself)
          const classTreeItemIds = [ClassCategory, ...parentClasses.map(cls => cls.uri)];
          // expand the class tree
          setExpandedClassTreeItemIds(classTreeItemIds);
        })();
      }
    }, [isLoadingForFirstTime, selectedDefinition, kgId]);

    // gets all tree items for the given category
    function getCategoryTreeItems(category) {
      return useMemo(() => {
        const toTreeItem = (obj, parentUris, parent) => {
          const additionalActions =
            ConceptTypeConfigurations[obj.type].getAdditionalActions?.(obj, kgPermissions, open, openSyncDialog) || [];
          const deleteAction = getDeleteAction(
            obj,
            isPreview => services.deleteKgConcept(kgId, obj.type, obj.shortUri, getParentUri(obj), isPreview),
            () => {
              if (parent) {
                reloadConcept(category, parentUris.slice(0, parentUris.length - 1), parent, false);
              } else {
                reloadCategory(category);
              }
            }
          );

          // Only show children for the first time if completely loaded (i.e. all types of children have been retrieved)
          const children =
            obj.treeChildren &&
            Object.values(obj.treeChildren).map(child => toTreeItem(child, [...parentUris, obj.uri], obj));

          return {
            id: obj.uri,
            label: obj.vklIdentifier,
            icon: IconsForVklType[obj.vklType],
            actions: [...additionalActions, deleteAction],
            childCount: ConceptTypeConfigurations[obj.type].getChildrenCount?.(obj),
            isLoading: loadingFlags[obj.uri],
            children: sort(children),
            onClick: treeItemClickHandler(obj, children, () => reloadConcept(category, parentUris, obj, true)),
            vklQualifiedIdentifier: obj.vklQualifiedIdentifier
          };
        };
        const children = Object.values(kgConcepts[category] || {}).map(obj => toTreeItem(obj, []));

        return [
          {
            id: category,
            label: TopLevelCategories[category].label,
            icon: TopLevelCategories[category].icon,
            isLoading: loadingFlags[category],
            children: sort(children)
          }
        ];
      }, [kgConcepts[category], loadingFlags, getDeleteAction]);
    }

    const classTreeItems = getCategoryTreeItems(ClassCategory);
    const functionTreeItems = getCategoryTreeItems(FunctionCategory);
    const variableTreeItems = getCategoryTreeItems(VariableCategory);
    const discoveryTreeItems = getCategoryTreeItems(DiscoveryCategory);
    const groundTruthTreeItems = getCategoryTreeItems(GroundTruthCategory);
    const boardTreeItems = getCategoryTreeItems(BoardCategory);

    return (
      <div className={classNames(cssClasses.root, className)}>
        {isLoadingForFirstTime ? (
          <Progress />
        ) : (
          <>
            <Tree
              items={classTreeItems}
              // expand the class tree
              expanded={expandedClassTreeItemIds}
              // if we're expanding the class tree beyond the root, select the innermost tree item
              selected={expandedClassTreeItemIds?.slice(1)[expandedClassTreeItemIds.length - 2]}
            />
            <Tree items={boardTreeItems} />
            <Tree items={functionTreeItems} />
            <Tree items={variableTreeItems} />
            <Tree items={discoveryTreeItems} />
            <Tree items={groundTruthTreeItems} />
          </>
        )}
      </div>
    );
  }
);

export default withTheme(withStyles(styles)(ConceptTree));
